import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Check, Briefcase, Store, Globe, Wallet, Crosshair, Ban } from "lucide-react";
import { CtaButton, MonoLabel, Tag } from "../components/kit";
import { ProfileSharpen } from "../components/ProfileSharpen";
import { BrandKitCard } from "../components/BrandKitCard";
import { WhatAgenzeeKnows } from "../components/WhatAgenzeeKnows";
import { PILLAR_COLORS } from "../components/EditorialCalendar";
import api from "../lib/api";

const MATURITY_LABELS = {
  has_website: "Website / landing page",
  active_socials: "Active social profiles",
  email_list: "Email list",
  ran_ads_before: "Ran paid ads before",
  pixel_installed: "Tracking pixel installed",
  gbp_claimed: "Google Business Profile claimed",
};

const SNAPSHOT_LABELS = [
  ["what_they_sell", "What you sell"],
  ["who_pays", "Who pays you"],
  ["revenue_streams", "Revenue streams"],
  ["niche", "Your niche"],
  ["what_they_are_NOT", "What you are NOT"],
];

const eur = (n) => "€" + Math.round(n || 0).toLocaleString("en-US");

const MetaChip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE5DB] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.05em] text-[#54505B] tabular-nums">
    <Icon size={11} strokeWidth={2} className="text-[#9A9591]" /> {children}
  </span>
);

const NotificationsCard = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/settings/notifications").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const toggle = async () => {
    const { data } = await api.put("/settings/notifications", { lead_email: !cfg.lead_email });
    setCfg(data);
  };
  return (
    <div className="card-soft mt-5 p-6" data-testid="settings-notifications-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <MonoLabel>NOTIFICATIONS</MonoLabel>
          <p className="font-bold text-[14.5px] text-[#181621] mt-2">Email me on every new landing-page lead</p>
          <p className="text-[12px] text-[#54505B] mt-1">
            {cfg.email_mode === "unconfigured" && "Email sending is not configured yet — leads are still saved in your Leads list."}
            {cfg.email_mode === "test" && "Sender domain not verified yet: emails only reach your registered address for now."}
            {cfg.email_mode === "live" && "Sent from your verified domain."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {cfg.email_mode === "test" && <Tag color="amber" data-testid="settings-email-test-badge">EMAIL: TEST MODE</Tag>}
          {cfg.email_mode === "unconfigured" && <Tag color="neutral" data-testid="settings-email-unconfigured-badge">NOT CONFIGURED</Tag>}
          <button
            onClick={toggle}
            className={`w-11 h-6 rounded-full transition-colors relative ${cfg.lead_email ? "bg-[#181621]" : "bg-[#E7E2D6]"}`}
            data-testid="settings-lead-email-toggle"
            aria-checked={cfg.lead_email}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${cfg.lead_email ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function BrandBrainSettings() {
  const [brain, setBrain] = useState(undefined);
  const [sharpenOpen, setSharpenOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/brand-brain").then(({ data }) => setBrain(data)).catch(() => setBrain(null));
  useEffect(() => { load(); }, []);

  const maturityDone = brain ? Object.keys(MATURITY_LABELS).filter((k) => brain.maturity?.[k]).length : 0;
  const maturityTotal = Object.keys(MATURITY_LABELS).length;

  return (
    <div className="p-6 sm:p-10 max-w-4xl" data-testid="brand-brain-settings-page">
      <MonoLabel>Settings · Brand Brain</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Everything I know about your business
      </h1>

      {brain === undefined && <div className="card-soft mt-8 p-10 animate-pulse h-40" data-testid="settings-loading" />}
      {brain === null && (
        <div className="card-soft mt-8 p-10 text-center" data-testid="settings-empty">
          <p className="font-bold text-[15px] text-[#181621]">No Brand Brain yet — complete onboarding first.</p>
        </div>
      )}

      {brain && (
        <>
          <div className="card-soft mt-8 p-6 sm:p-8" data-testid="settings-identity-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xl font-extrabold text-[#181621]" data-testid="settings-business-name">{brain.business_name || "—"}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <MetaChip icon={Briefcase}>{brain.business_type}</MetaChip>
                  <MetaChip icon={Store}>{brain.business_model?.replace(/_/g, " ").toUpperCase()}</MetaChip>
                  <MetaChip icon={Globe}>{brain.country} · {brain.market_scope?.toUpperCase()}</MetaChip>
                  <MetaChip icon={Wallet}>{eur(brain.monthly_budget)}/MO</MetaChip>
                  <MetaChip icon={Crosshair}>GOAL: {brain.primary_goal?.toUpperCase()}</MetaChip>
                </div>
              </div>
              <CtaButton className="!py-2 !px-4 text-[13px]" onClick={() => setSharpenOpen(true)} data-testid="settings-edit-btn">
                <Pencil size={13} strokeWidth={1.8} /> Edit description & snapshot
              </CtaButton>
            </div>
            {brain.business_description && (
              <p className="text-[13.5px] text-[#54505B] mt-5 leading-relaxed" data-testid="settings-description">{brain.business_description}</p>
            )}
          </div>

          <div className="card-soft mt-5 p-6 sm:p-8" data-testid="settings-snapshot-card">
            <MonoLabel>BUSINESS SNAPSHOT — THE PRIMARY TRUTH BEHIND EVERY PLAN</MonoLabel>
            {brain.snapshot ? (
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5 mt-6">
                {SNAPSHOT_LABELS.map(([key, label]) => {
                  const isNot = key === "what_they_are_NOT";
                  return (
                    <div key={key} className={`${key === "what_they_sell" ? "sm:col-span-2" : ""} ${isNot ? "sm:col-span-2 border-l-[3px] border-l-[#E0603A] pl-4" : ""}`}>
                      <p className={`text-[10px] font-bold tracking-[0.1em] uppercase flex items-center gap-1.5 ${isNot ? "text-[#E0603A]" : "text-[#9A9591]"}`}>
                        {isNot && <Ban size={10} strokeWidth={2.5} />} {label}
                      </p>
                      <p className="text-[13.5px] text-[#181621] mt-1.5 leading-relaxed" data-testid={`settings-snapshot-${key}`}>
                        {Array.isArray(brain.snapshot[key]) ? brain.snapshot[key].join(" · ") : brain.snapshot[key]}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[13.5px] text-[#54505B] mt-4" data-testid="settings-no-snapshot">
                No confirmed snapshot yet — hit "Edit description & snapshot" to build it. Your plans get dramatically sharper.
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <div className="card-soft p-6" data-testid="settings-maturity-card">
              <div className="flex items-center justify-between gap-3">
                <MonoLabel>MARKETING MATURITY</MonoLabel>
                <span className="text-[11.5px] font-bold text-[#181621] tabular-nums" data-testid="settings-maturity-progress">{maturityDone} / {maturityTotal} complete</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden mt-3">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(maturityDone / maturityTotal) * 100}%`, background: "linear-gradient(90deg, #E0603A, #0B67FF)" }} />
              </div>
              <div className="space-y-2.5 mt-4">
                {Object.entries(MATURITY_LABELS).map(([key, label]) => {
                  const on = !!brain.maturity?.[key];
                  return (
                    <div key={key} className="flex items-center gap-2.5" data-testid={`settings-maturity-${key.replace(/_/g, "-")}`}>
                      {on ? (
                        <span className="w-[18px] h-[18px] rounded-full bg-[#181621] flex items-center justify-center shrink-0">
                          <Check size={11} strokeWidth={2.5} className="text-white" />
                        </span>
                      ) : (
                        <span className="w-[18px] h-[18px] rounded-full border border-[#E7E2D6] bg-white shrink-0" />
                      )}
                      <span className={`text-[13px] ${on ? "text-[#181621] font-semibold" : "text-[#9A9591]"}`}>{label}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11.5px] text-[#9A9591] mt-4">Flags update automatically when you complete setup tasks in the Action Plan.</p>
            </div>

            <div className="card-soft p-6" data-testid="settings-pillars-card">
              <MonoLabel>CONTENT PILLARS</MonoLabel>
              {(brain.content_pillars || []).length ? (
                <div className="flex flex-wrap gap-2 mt-4">
                  {brain.content_pillars.map((p, i) => (
                    <span key={p} className="inline-flex items-center gap-2 rounded-full border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12.5px] font-bold text-[#181621]">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: `var(--${PILLAR_COLORS[i % PILLAR_COLORS.length]}-fg)` }} />
                      {p}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-[#54505B] mt-4">No pillars yet.</p>
              )}
              <p className="text-[11.5px] text-[#9A9591] mt-4">Manage pillars in Organic Studio → Editorial Plan.</p>
            </div>
          </div>

          <BrandKitCard key={JSON.stringify(brain.brand_kit || {})} kit={brain.brand_kit || {}} onSaved={load} />

          <WhatAgenzeeKnows />

          <NotificationsCard />

          <ProfileSharpen
            open={sharpenOpen}
            onOpenChange={setSharpenOpen}
            brain={brain}
            onDone={load}
            onRegenerate={async () => {
              try {
                await api.post("/strategy/generate", {});
              } catch { /* strategy page will surface state */ }
              navigate("/app/ceo/strategy");
            }}
          />
        </>
      )}
    </div>
  );
}
